import { AxiosResponse, CancelToken } from 'axios';
import { BUSINESS_PYTHON, CONTENT } from './axios';
import { config } from 'config/config.local';
import {
  DeadlineApplyMode,
  DeadlinePolicy,
  DeadlineRemoveResponse,
  DeadlineUpdateResponse,
  IAccountDeadlinePolicyGetResponse,
  IPlaylist,
  IPlaylistUserDetails,
  IPlaylistUsersResponse,
} from 'contexts/playlists/types';
import { IPlaylistChangelogContent } from 'contexts/content/types';

export interface IGetPlaylists {
  results: IPlaylist[];
  count: number;
}

export interface IPlaylistsFetchParams {
  page: string | number | null;
  page_size: string | number | null;
  name__icontains: string | null;
  business_account_id?: string;
}

export interface IPlaylistFetchParams {
  id: string | number | null;
  business_account_id?: string;
}

export interface IAssignUsersParams {
  business_account_id?: string;
  id: string;
  user_ids: string[];
}

export interface IAssignTeamsParams {
  business_account_id?: string;
  id: string;
  team_ids: string[];
}

export interface IDeletePlaylist {
  business_account_id?: string;
  id: string;
}

export interface IDuplicatePlaylist {
  playlist_id: string;
  business_account_id: string;
  name: string;
  description: string;
  color: string;
}

export interface IPlaylistAssignedTeamsResponse {
  total_teams_assigned: number;
  teams_assigned: {
    id: string;
    name: string;
    active_users_count: number | null;
    deadline_policy: (DeadlinePolicy & { activated_at?: string }) | null;
  }[];
}

export type PlaylistRuleScope = 'account' | 'team';

export interface IPlaylistRuleRequest {
  playlist_id: string;
  scope: PlaylistRuleScope;
  is_active?: boolean;
  team_ids?: string[];
}

export interface IPlaylistRuleResponse {
  id?: string;
  playlist_id?: string;
  scope?: PlaylistRuleScope;
  is_active?: boolean;
  status?: string;
  teams_assigned?: number;
  total_users_to_process?: number;
}

export const getPlaylists = ({
  business_account_id,
  ...params
}: IPlaylistsFetchParams): Promise<AxiosResponse<IGetPlaylists>> => {
  return CONTENT.get(
    `${config.routes.playlists.getPlaylists}?business_account_id=${business_account_id}`,
    { params }
  );
};

// The timeout is opt-in: it belongs to the background duration-refresh loop, not to the
// interactive load, where a large playlist can legitimately take longer than the bound.
export const getPlaylist = (
  params: IPlaylistFetchParams,
  options?: { timeoutMs?: number }
): Promise<AxiosResponse<IPlaylist>> => {
  return CONTENT.get(
    `${config.routes.playlists.getPlaylists}/${params.id}?business_account_id=${params.business_account_id}`,
    options?.timeoutMs ? { timeout: options.timeoutMs } : undefined
  );
};

export const createPlaylist = (params: IPlaylist): Promise<AxiosResponse<IPlaylist>> => {
  const payload = { ...params, origin: 'DASHBOARD' };
  return CONTENT.post(
    `${config.routes.playlists.getPlaylists}?business_account_id=${params.business_account_id}`,
    payload
  );
};

export const updatePlaylist = (params: IPlaylist): Promise<AxiosResponse<IPlaylist>> => {
  return CONTENT.put(
    `${config.routes.playlists.getPlaylists}/${params.id}?business_account_id=${params.business_account_id}`,
    { ...params }
  );
};

export const editPlaylist = (params: IPlaylist): Promise<AxiosResponse<IPlaylist>> => {
  return CONTENT.patch(
    `${config.routes.playlists.getPlaylists}/${params.id}?business_account_id=${params.business_account_id}`,
    { ...params }
  );
};

export const deletePlaylist = (params: IDeletePlaylist): Promise<AxiosResponse<IPlaylist>> => {
  return CONTENT.delete(
    `${config.routes.playlists.getPlaylists}/${params.id}?business_account_id=${params.business_account_id}`
  );
};

export const assignUsers = (params: IAssignUsersParams): Promise<AxiosResponse<IPlaylist>> => {
  return CONTENT.post(
    `${config.routes.playlists.getPlaylists}/${params.id}/assign?business_account_id=${params.business_account_id}`,
    { user_ids: params.user_ids }
  );
};

export const duplicatePlaylist = ({
  playlist_id,
  business_account_id,
  name,
  description,
  color,
}: IDuplicatePlaylist): Promise<AxiosResponse<IPlaylist>> => {
  const payload = { name, description, color };
  const basePath = `${config.routes.playlists.getPlaylists}/${playlist_id}/duplicate`;

  return CONTENT.post<IPlaylist>(basePath, payload, {
    params: { business_account_id },
  }).catch((error) => {
    if (error?.response?.status === 404) {
      return CONTENT.post<IPlaylist>(`${basePath}/`, payload, {
        params: { business_account_id },
      });
    }
    throw error;
  });
};

export const assignTeams = (
  params: IAssignTeamsParams
): Promise<AxiosResponse<IPlaylistRuleResponse>> => {
  return createPlaylistRule({
    playlist_id: params.id,
    scope: 'team',
    team_ids: params.team_ids,
  });
};

export const unAssignUsers = (params: IAssignUsersParams): Promise<AxiosResponse<IPlaylist>> => {
  return CONTENT.post(
    `${config.routes.playlists.getPlaylists}/${params.id}/unassign?business_account_id=${params.business_account_id}`,
    { ...params }
  );
};

export const getPlaylistUsersList = ({
  account_id,
  playlist_id,
  page,
  page_size,
  deadline_status,
  ordering,
  cancelToken,
}: {
  account_id: string;
  playlist_id: string;
  page?: string | number;
  page_size?: string | number;
  deadline_status?: string;
  ordering?: string;
  cancelToken?: CancelToken;
}): Promise<AxiosResponse<IPlaylistUsersResponse>> => {
  return CONTENT.get(
    `${config.routes.playlists.getPlaylists}/${playlist_id}/users?business_account_id=${account_id}`,
    {
      cancelToken,
      params: {
        page,
        page_size,
        ...(deadline_status ? { deadline_status } : {}),
        ...(ordering ? { ordering } : {}),
      },
    }
  );
};

export const getPlaylistAssignedTeams = ({
  account_id,
  playlist_id,
}: {
  account_id: string;
  playlist_id: string;
}): Promise<AxiosResponse<IPlaylistAssignedTeamsResponse>> => {
  return BUSINESS_PYTHON.get(
    `${config.routes.playlists.getPlaylists}/${playlist_id}/assigned-teams/?business_account_id=${account_id}`
  );
};

export const getPlaylistUserDetails = ({
  business_account_id,
  playlist_id,
  user_id,
}: {
  business_account_id: string;
  playlist_id: string;
  user_id: string;
}): Promise<AxiosResponse<IPlaylistUserDetails>> => {
  return CONTENT.get(`${config.routes.playlists.getPlaylists}/${playlist_id}/users/${user_id}`, {
    params: { business_account_id },
  });
};

export const getLearningPathDetail = (id: string): Promise<AxiosResponse<unknown>> => {
  return CONTENT.get(`${config.routes.playlists.getLearningPath}/${id}`);
};

export const userAssignedPlaylist = (user_id: string): Promise<AxiosResponse<IGetPlaylists>> => {
  return CONTENT.get(`${config.routes.playlists.getPlaylists}/assigned?user_id=${user_id}`);
};

export const fetchPlaylistChangelog = (
  playlist_id: string
): Promise<AxiosResponse<{ results: IPlaylistChangelogContent[] }>> => {
  return CONTENT.get(`${config.routes.playlists.getPlaylists}/${playlist_id}/content-changes`);
};

export const createPlaylistRule = (
  payload: IPlaylistRuleRequest
): Promise<AxiosResponse<IPlaylistRuleResponse>> => {
  return BUSINESS_PYTHON.post(config.routes.playlists.playlistRules, payload);
};

export const updatePlaylistRule = (
  payload: IPlaylistRuleRequest
): Promise<AxiosResponse<IPlaylistRuleResponse>> => {
  return BUSINESS_PYTHON.delete(config.routes.playlists.playlistRules, {
    data: payload,
  });
};

const getDeadlinePolicyUrl = (playlistId: string): string =>
  `${config.routes.playlists.getPlaylists}/${playlistId}/deadline-policy`;

const assertTeamIdsForTeamScope = (scope: PlaylistRuleScope, team_ids?: string[]): void => {
  if (scope === 'team' && !team_ids?.length) {
    throw new Error('team_ids is required when scope is "team"');
  }
};

export interface IGetDeadlinePolicyParams {
  scope: PlaylistRuleScope;
  team_ids?: string[];
}

export interface IDeadlinePolicyResult {
  accountPolicy: DeadlinePolicy | null;
  teamPolicies: Array<DeadlinePolicy & { team_id: string }>;
}

// GET /api/v1/playlists/{playlist_id}/deadline-policy — a missing policy (404 or null body)
// means "No deadline" and resolves to empty rather than rejecting.
export const getDeadlinePolicy = async (
  playlistId: string,
  { scope, team_ids }: IGetDeadlinePolicyParams
): Promise<IDeadlinePolicyResult> => {
  assertTeamIdsForTeamScope(scope, team_ids);

  try {
    const response = await BUSINESS_PYTHON.get<IAccountDeadlinePolicyGetResponse | null>(
      getDeadlinePolicyUrl(playlistId),
      { params: scope === 'team' ? { scope, team_ids } : { scope } }
    );
    return {
      accountPolicy: response.data?.account_policy ?? null,
      teamPolicies: response.data?.team_policies ?? [],
    };
  } catch (error) {
    const typedError = error as { response?: { status?: number } };
    if (typedError?.response?.status === 404) {
      return { accountPolicy: null, teamPolicies: [] };
    }
    throw error;
  }
};

export interface IUpsertDeadlinePolicyParams {
  playlistId: string;
  training_pace: number;
  apply_to: DeadlineApplyMode;
  scope: PlaylistRuleScope;
  team_ids?: string[];
}

// POST /api/v1/playlists/{playlist_id}/deadline-policy (upsert) — only 'recommended' exists
// at the default scope.
export const upsertDeadlinePolicy = ({
  playlistId,
  training_pace,
  apply_to,
  scope,
  team_ids,
}: IUpsertDeadlinePolicyParams): Promise<AxiosResponse<DeadlinePolicy>> => {
  assertTeamIdsForTeamScope(scope, team_ids);

  return BUSINESS_PYTHON.post(getDeadlinePolicyUrl(playlistId), {
    type: 'recommended',
    training_pace,
    scope,
    ...(scope === 'team' ? { team_ids } : {}),
    apply_to,
  });
};

export interface IDeleteDeadlinePolicyParams {
  scope: PlaylistRuleScope;
  team_ids?: string[];
  apply_to: DeadlineApplyMode;
}

// DELETE /api/v1/playlists/{playlist_id}/deadline-policy — the "No deadline" save path.
// apply_to decides the propagation to users already assigned: "all" clears every current
// deadline (custom included), "future" is a no-op for them (RFC §3.1.3, business-api#1165).
export const deleteDeadlinePolicy = (
  playlistId: string,
  { scope, team_ids, apply_to }: IDeleteDeadlinePolicyParams
): Promise<AxiosResponse<void>> => {
  assertTeamIdsForTeamScope(scope, team_ids);

  return BUSINESS_PYTHON.delete(getDeadlinePolicyUrl(playlistId), {
    data: { ...(scope === 'team' ? { scope, team_ids } : { scope }), apply_to },
  });
};

export interface IPostDeadlineParams {
  playlist_id: string;
  user_ids: string[];
  due_date: string;
  deadline_type: 'custom' | 'recommended';
  training_pace?: number;
  business_account_id: string;
}

// Set or update deadline — handles both individual (1 user_id) and bulk (N user_ids).
// Feature flag: playlist-deadlines-mvp — callers must check this flag via useFlags() before invoking.
// Error handling: callers are responsible for catching errors and displaying feedback via Notification:
//   Notification({ text: 'Failed to update deadline', description: error.message, type: 'error' })
// POST /api/v1/playlists/{playlist_id}/users/deadline/
export const postDeadline = async ({
  playlist_id,
  user_ids,
  due_date,
  deadline_type,
  training_pace,
  business_account_id,
}: IPostDeadlineParams): Promise<AxiosResponse<DeadlineUpdateResponse>> => {
  const url = `${config.routes.playlists.getPlaylists}/${playlist_id}/users/deadline?business_account_id=${business_account_id}`;
  const payload = {
    user_ids,
    due_date,
    deadline_type,
    ...(deadline_type === 'recommended' && training_pace != null ? { training_pace } : {}),
  };

  return CONTENT.post(url, payload);
};

export interface IDeleteDeadlineParams {
  playlist_id: string;
  user_ids: string[];
  business_account_id: string;
}

// Remove deadline — handles both individual (1 user_id) and bulk (N user_ids).
// Feature flag: playlist-deadlines-mvp — callers must check this flag via useFlags() before invoking.
// Error handling: callers are responsible for catching errors and displaying feedback via Notification:
//   Notification({ text: 'Failed to update deadline', description: error.message, type: 'error' })
// DELETE /api/v1/playlists/{playlist_id}/users/deadline
export const deleteDeadline = async ({
  playlist_id,
  user_ids,
  business_account_id,
}: IDeleteDeadlineParams): Promise<AxiosResponse<DeadlineRemoveResponse>> => {
  const url = `${config.routes.playlists.getPlaylists}/${playlist_id}/users/deadline?business_account_id=${business_account_id}`;
  const payload = { user_ids };

  return CONTENT.delete(url, { data: payload });
};
